import { useMemo } from 'react';
import { isNavItemActive } from '../../config/nav/isNavItemActive';
import SidebarSection from './SidebarSection';
import { useSidebarSectionCollapse } from './useSidebarSectionCollapse';

/** Maquette : pas de titre pour le tableau de bord (Dashboard + Analytics). */
export const SECTIONS_WITHOUT_HEADER = new Set(['dashboard']);

/** Sections toujours visibles — pas de repli. */
export const SECTIONS_ALWAYS_OPEN = new Set(['dashboard']);

function findActiveSectionId(sections, location) {
  for (const section of sections) {
    if (section.items.some((item) => isNavItemActive(item.href, location))) {
      return section.id;
    }
  }
  return null;
}

/**
 * @param {{
 *   sections: Array<{ id: string, title?: string | null, items: object[] }>,
 *   location: import('react-router-dom').Location,
 *   renderItem: (item: object, ctx: { isActive: boolean }) => import('react').ReactNode,
 * }} props
 */
const SidebarNav = ({ sections, location, renderItem }) => {
  const collapsibleSectionIds = useMemo(
    () => sections
      .filter((section) => section.title && !SECTIONS_ALWAYS_OPEN.has(section.id))
      .map((section) => section.id),
    [sections],
  );

  const activeSectionId = useMemo(
    () => findActiveSectionId(sections, location),
    [sections, location],
  );

  const { isSectionOpen, toggleSection } = useSidebarSectionCollapse(
    collapsibleSectionIds,
    activeSectionId,
  );

  return (
    <nav className="sidebar-nav-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-4">
      {sections.map((section, sectionIndex) => {
        const collapsible = collapsibleSectionIds.includes(section.id);
        const open = isSectionOpen(section.id);
        // Indicateur futur (Lot B′) : section.id === activeSectionId && !open
        // (ou isActiveSectionCollapsed du hook lorsque section.id === activeSectionId)

        return (
          <SidebarSection
            key={section.id}
            title={!SECTIONS_WITHOUT_HEADER.has(section.id) ? section.title : null}
            showSeparator={sectionIndex > 0}
            collapsible={collapsible}
            isOpen={open}
            onToggle={() => toggleSection(section.id)}
          >
            {section.items.map((item) =>
              renderItem(item, { isActive: isNavItemActive(item.href, location) }),
            )}
          </SidebarSection>
        );
      })}
    </nav>
  );
};

export default SidebarNav;
